// How a preset reads in the UI: its name (a demo preset is named after its layout, translated), its
// section type (built-in names are ours; an Admin's custom type carries its own RU and UZ names), status.
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { PresetOut, PresetStatus, SectionTypeOut } from '@/api/types';
import { isBuiltinSectionType, isLayout, type Tone } from '@/design-system';
import { sectionTypesQuery } from './api';

export const STATUS_TONE: Record<PresetStatus, Tone> = { published: 'ok', draft: 'mute', review: 'warn', archived: 'mute' };

type Named = Pick<PresetOut, 'origin' | 'layout' | 'mirrored' | 'name'>;

export function usePresetLabels() {
  const { t, i18n } = useTranslation('library');
  const types = useQuery(sectionTypesQuery).data;
  const uz = i18n.language === 'uz';

  /** The design's demo presets are named «‹layout›» or «‹layout› · зеркально» (buildLib), in the UI's language. */
  const name = useCallback(
    (p: Named) => {
      if (p.origin !== 'demo' || !isLayout(p.layout)) return p.name;
      const base = t(`layouts.${p.layout}`);
      return p.mirrored ? t('mirrored', { name: base }) : base;
    },
    [t],
  );

  const section = useCallback(
    (key: string) => {
      if (isBuiltinSectionType(key)) return t(`sections.${key}` as 'sections.hero');
      const row: SectionTypeOut | undefined = types?.find((x) => x.key === key);
      return row ? (uz ? row.name_uz : row.name_ru) : key;
    },
    [t, types, uz],
  );

  return { name, section, types };
}
